import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import MomentsGallery from "@/components/MomentsGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Shield, Pencil, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";

function Item({ text, sub, testId, onSave, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(text);
  useEffect(() => setVal(text), [text]);
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card/40 px-4 py-3" data-testid={testId}>
      <div className="flex-1 min-w-0">
        {edit ? (
          <Input value={val} onChange={(e) => setVal(e.target.value)} autoFocus className="h-9" data-testid={`${testId}-input`}
            onKeyDown={(e) => { if (e.key === "Enter") { onSave(val); setEdit(false); } }} />
        ) : <p className="text-sm leading-relaxed">{text}</p>}
        {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
      </div>
      {edit ? (
        <>
          <button onClick={() => { onSave(val); setEdit(false); }} aria-label="Save" className="text-primary mt-2" data-testid={`${testId}-save`}><Check className="w-4 h-4" /></button>
          <button onClick={() => { setVal(text); setEdit(false); }} aria-label="Cancel" className="text-muted-foreground mt-2"><X className="w-4 h-4" /></button>
        </>
      ) : (
        <>
          <button onClick={() => setEdit(true)} aria-label="Edit" className="text-muted-foreground hover:text-foreground mt-0.5 transition-colors" data-testid={`${testId}-edit`}><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} aria-label="Forget" className="text-muted-foreground hover:text-destructive mt-0.5 transition-colors" data-testid={`${testId}-delete`}><Trash2 className="w-4 h-4" /></button>
        </>
      )}
    </div>
  );
}

const Section = ({ title, hint, children, testId }) => (
  <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10" data-testid={testId}>
    <h2 className="font-serif text-2xl mb-1">{title}</h2>
    <p className="text-xs text-muted-foreground mb-4">{hint}</p>
    <div className="space-y-2">{children}</div>
  </motion.section>
);

export default function Memory() {
  const [m, setM] = useState(null);
  const [newFact, setNewFact] = useState("");
  useEffect(() => { api.get("/memory/all").then((r) => setM(r.data)).catch(() => toast.error("Couldn't load your memory")); }, []);
  const run = async (p) => { try { const { data } = await p; setM(data); } catch { toast.error("Couldn't update that"); } };

  const saveFacts = (facts) => run(api.put("/memory/facts", { facts }));
  const forgetAll = async () => {
    try { await api.delete("/memory/everything"); setM({ facts: [], sessions: [], moments: [] }); toast.success("Xcelio has forgotten everything."); }
    catch { toast.error("Couldn't clear memory"); }
  };

  if (!m) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="memory-page">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Your privacy</p>
          <h1 className="font-serif text-4xl sm:text-5xl">What Xcelio remembers</h1>
          <p className="text-muted-foreground mt-3 flex items-center gap-1.5"><Shield className="w-4 h-4" /> This is yours. Edit or delete anything, anytime.</p>
        </div>

        <Section title="About your life" hint="The things you've told Xcelio that she keeps in mind." testId="memory-facts">
          {m.facts.map((f, i) => (
            <Item key={`${i}-${f}`} text={f} testId={`memory-fact-${i}`}
              onSave={(v) => saveFacts(m.facts.map((x, j) => (j === i ? v : x)).filter((x) => x.trim()))}
              onDelete={() => saveFacts(m.facts.filter((_, j) => j !== i))} />
          ))}
          <form className="flex gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); if (newFact.trim()) { saveFacts([...m.facts, newFact.trim()]); setNewFact(""); } }}>
            <Input value={newFact} onChange={(e) => setNewFact(e.target.value)} placeholder="Add something you'd like her to remember…" data-testid="memory-fact-new" />
            <Button type="submit" variant="outline" size="icon" className="rounded-full shrink-0" disabled={!newFact.trim()} data-testid="memory-fact-add"><Plus className="w-4 h-4" /></Button>
          </form>
        </Section>

        <Section title="Your conversations" hint="Her private notes after each conversation, so she can pick up where you left off." testId="memory-sessions">
          {m.sessions.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          {m.sessions.map((s, i) => (
            <Item key={`${i}-${s.date}`} text={s.summary} sub={s.date} testId={`memory-session-${i}`}
              onSave={(v) => run(api.put(`/memory/sessions/${i}`, { text: v }))}
              onDelete={() => run(api.put(`/memory/sessions/${i}`, { text: "" }))} />
          ))}
        </Section>

        <Section title="Key moments" hint="Breakthroughs and moments that mattered on your journey." testId="memory-moments">
          {m.moments.length === 0 && <p className="text-sm text-muted-foreground">No moments yet.</p>}
          {m.moments.map((x) => (
            <Item key={x.id} text={x.text} sub={x.date} testId={`memory-moment-${x.id}`}
              onSave={(v) => run(api.put(`/memory/moments/${x.id}`, { text: v }))}
              onDelete={() => run(api.put(`/memory/moments/${x.id}`, { text: "" }))} />
          ))}
        </Section>

        <Section title="Lines worth keeping" hint="Standout lines from your Coach. Share any of them as a card." testId="memory-coach-lines">
          <MomentsGallery />
        </Section>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="memory-forget-all">
              <Trash2 className="w-4 h-4 mr-1.5" /> Forget everything
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Forget everything?</AlertDialogTitle>
              <AlertDialogDescription>Xcelio will forget your life details, conversation notes, key moments and chat history. This can't be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="memory-forget-cancel">Keep my memory</AlertDialogCancel>
              <AlertDialogAction onClick={forgetAll} data-testid="memory-forget-confirm">Forget everything</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
